// P:\0PJ\synforge\frontend\contexts\LocaleContext.tsx v1 2026.06.23
'use client';

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LOCALES = ["en", "ko", "ja", "zh", "zh-HK", "es", "fr", "de", "pt", "ar", "hi"];

interface LocaleContextType {
  locale:    string;
  t:         (key: string) => string;
  setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextType>({
  locale:    'en',
  t:         (key) => key,
  setLocale: () => {},
});

// 중첩 키 접근 — "nav.home", "dashboard.title" 등
function getNestedValue(obj: any, key: string): string {
  const val = key.split('.').reduce((acc, k) => acc?.[k], obj);
  return typeof val === 'string' ? val : key;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale,   setLocaleState] = useState('en');
  const [messages, setMessages]    = useState<Record<string, any>>({});
  const [ready,    setReady]       = useState(false);

  // 메시지 로드
  const loadMessages = useCallback(async (loc: string) => {
    try {
      const msgs = await import(`../messages/${loc}.json`);
      setMessages(msgs.default);
    } catch {
      const msgs = await import('../messages/en.json');
      setMessages(msgs.default);
    }
  }, []);

  // 초기화 — 쿠키에서 locale 읽기
  useEffect(() => {
    const cookieLocale = document.cookie
      .split('; ')
      .find((row) => row.startsWith('locale='))
      ?.split('=')[1];
    const loc = LOCALES.includes(cookieLocale || '') ? cookieLocale! : 'en';
    setLocaleState(loc);
    loadMessages(loc).then(() => setReady(true));
  }, []);

  // locale 변경 — 깜박임 없이 즉시 적용
  const setLocale = useCallback((loc: string) => {
    if (!LOCALES.includes(loc)) return;
    document.cookie = `locale=${loc}; path=/; max-age=31536000; SameSite=Lax`;
    setLocaleState(loc);
    loadMessages(loc);
  }, [loadMessages]);

  // 번역 함수
  const t = useCallback((key: string) => {
    return getNestedValue(messages, key);
  }, [messages]);

  if (!ready) return null;

  return (
    <LocaleContext.Provider value={{ locale, t, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export const useLocale = () => useContext(LocaleContext);